import React, { useContext, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import lodash from 'lodash';

import CompanyDetails from 'src/components/ubo/CompanyDetails';
import { detailParams } from 'src/types/ICompany';
import RiskRating from 'src/components/ubo/RiskRating';
import {
  formatNumber,
  getNominalShareCapital,
  getPreferredRegistrationNumber,
  getRegistrationNumberTooltip,
  formatDate,
  getMatchByName,
} from '../services/companyReportDetail.service';
import {
  COMPANY_INFORMATION_HEADING,
  DEFAULT_EMPTY_VALUE,
  PROVIDER_SOURCES,
  INITIAL_BADGE_VALUE,
  DEFAULT_PREV_FINANCIAL_LIMIT,
  FINANCIAL_INFO_DNB_NUMBER_FIELDS,
  CREDIT_SUMMARY_NUMBER_FIELDS,
} from '../constants/companyReport';
import { companyReportsAxiosInstance } from 'src/services/axiosInstance.service';

import * as companyReportDetailService from '../services/companyReportDetail.service';
import { pdfDate } from '../utils/formatting.utils';
import { handleUBOErrors } from 'src/services/errors.service';
import * as notify from 'src/services/notification.service';
import { useEffectOnce } from 'src/hooks/useEffectOnce';
import { EntityType } from 'src/enums';
import { AppContext } from 'src/utils';
import * as dnbMonitoringService from 'src/services/dnbMonitoring.service';

const CompanyReportDetailPage: React.FC = () => {
  const { currentUser } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const [detailData, setDetailData] = useState<any>({});
  const [detailIsLoading, setDetailIsLoading] = useState(false);
  const [isMonitored, setIsMonitored] = useState<any>({});
  const [isMonitoringLoading, setIsMonitoringLoading] = useState(false);
  const [pdfIsLoading, setPdfIsLoading] = useState(false);
  const [CRADetails, updateCRADetails] = useState({ overallRiskRating: INITIAL_BADGE_VALUE });
  const [pdfDownloadBtn, addDownloadPdfButton] = useState(false);
  const [screenedEntities, updateScreenedEntities] = useState<any>({});
  const [registrationNumber, setRegistrationNumber] = useState<any>('');
  const [registrationNumberTooltip, setRegistrationNumberTooltip] = useState('');
  const [pdfReviewDate] = useState(new Date());
  const [craDefaultValues, setCraDefaultValues] = useState({
    COUNTRY: undefined,
    CUSTOMER: undefined,
    DELIVERY: undefined,
    SCREENING: undefined,
    SIC: undefined,
    PRODUCT: undefined,
  });

  let companyEmails: Array<string> = detailData?.companyProfile?.companyProfile?.companyEmail;
  let phoneNumbers: Array<string> = detailData?.companyProfile?.companyProfile?.phoneNumber;
  let websites: Array<string> = detailData?.companyProfile?.companyProfile?.websites;
  let numberOfEmployees: string = detailData?.companyProfile?.companyProfile?.numberOfEmployees
    ?.length
    ? formatNumber(detailData?.companyProfile?.companyProfile?.numberOfEmployees[0]?.value)
    : DEFAULT_EMPTY_VALUE;

  let screenedBusinessEntitiesFormatted = screenedEntities?.screenedBusinessEntities?.map(
    (entity: any) => ({
      name: entity?.name,
      type: EntityType.Business,
      match: getMatchByName(entity?.name, screenedEntities?.screenedBusinessEntities),
      totalMatches: entity?.total_matches,
    }),
  );

  let screenedIndividualEntitiesFormatted = screenedEntities?.screenedIndividualEntities?.map(
    (entity: any) => ({
      name: entity?.name,
      type: EntityType.Individual,
      match: getMatchByName(entity?.name, screenedEntities?.screenedIndividualEntities),
      totalMatches: entity?.total_matches,
    }),
  );

  const directors =
    detailData.companyProfile?.companyProfile?.directorAndShareDetails?.directors ?? [];
  let directorsFormatted = directors.map((director: any) => ({
    name: director.name ?? DEFAULT_EMPTY_VALUE,
    match: getMatchByName(
      director.firstName + ' ' + director.middleName + ' ' + director.lastName,
      screenedEntities?.screenedIndividualEntities,
    ),
    dateOfBirth: formatDate(director.dateOfBirth),
    position: director.position ?? DEFAULT_EMPTY_VALUE,
    dateAppointed: formatDate(director.dateAppointed),
    nationality: director.nationality ?? DEFAULT_EMPTY_VALUE,
    address: director.address ?? DEFAULT_EMPTY_VALUE,
  }));

  const beneficialOwners = detailData?.companyProfile?.companyProfile?.beneficialOwners ?? [];
  let beneficialOwnersFormatted = beneficialOwners.map((beneficialOwner: any) => ({
    name: beneficialOwner.name ?? DEFAULT_EMPTY_VALUE,
    match: getMatchByName(
      beneficialOwner.name,
      beneficialOwner?.shareholderType === 'company'
        ? screenedEntities?.screenedBusinessEntities
        : screenedEntities?.screenedIndividualEntities,
    ),
    directOwnership: beneficialOwner.directOwnership ?? DEFAULT_EMPTY_VALUE,
    indirectOwnership: beneficialOwner.indirectOwnership ?? DEFAULT_EMPTY_VALUE,
    beneficialOwnershipPercentage:
      beneficialOwner.beneficialOwnershipPercentage ?? DEFAULT_EMPTY_VALUE,
    address: beneficialOwner.address ?? DEFAULT_EMPTY_VALUE,
  }));

  const beneficialOwnersRelations = detailData?.companyProfile?.companyProfile?.beneficialOwnersRelations ?? [];
  const beneficialOwnersRelationsFormatted: any[] = [];
  beneficialOwnersRelations.forEach((beneficialOwner: any) => {
    beneficialOwner.relations.forEach((relation: any) => {
      relation.natureOfControlType.forEach((controlType: any) => {
        const currentRelation = {
          name: beneficialOwner.name ?? DEFAULT_EMPTY_VALUE,
          description: controlType.description ?? DEFAULT_EMPTY_VALUE
        };

        beneficialOwnersRelationsFormatted.push(currentRelation);
      });
    });
  });

  const extendedGroupStructure = detailData?.companyProfile?.companyProfile?.extendedGroupStructure;
  let annualRevenue = extendedGroupStructure?.map((egs: any) =>
    egs?.annualRevenue?.length && egs.annualRevenue[0]?.yearlyRevenues?.length
      ? formatNumber(egs.annualRevenue[0]?.yearlyRevenues?.[0].value)
      : DEFAULT_EMPTY_VALUE,
  );
  const extendedGroupStructureFormatted = extendedGroupStructure?.map(
    (egs: any, index: number) => ({
      level: egs?.level ?? DEFAULT_EMPTY_VALUE,
      companyName: egs.companyName ?? DEFAULT_EMPTY_VALUE,
      countryOfIncorporation: egs?.countryOfIncorporation ?? DEFAULT_EMPTY_VALUE,
      corporateLinkageDescription: egs.corporateLinkage?.length
        ? egs.corporateLinkage[0].description
        : DEFAULT_EMPTY_VALUE,
      annualRevenue: annualRevenue[index] ?? DEFAULT_EMPTY_VALUE,
      latestAnnualAccounts: formatDate(egs.latestAnnualAccounts),
    }),
  );

  let financialInformation = [].concat(
    detailData?.companyProfile?.companyProfile?.financialInformationV2?.latestFinancials,
    detailData?.companyProfile?.companyProfile?.financialInformationV2?.previousFinancials?.slice(
      0,
      DEFAULT_PREV_FINANCIAL_LIMIT,
    ),
  );

  for (let propertName of FINANCIAL_INFO_DNB_NUMBER_FIELDS) {
    for (let i = 0; i < financialInformation?.length; i++) {
      let value: any = lodash.get(financialInformation[i], propertName.split('.'));
      if (value && !value?.toString().includes(',')) {
        lodash.set(financialInformation[i], propertName.split('.'), formatNumber(value));
      }
    }
  }

  const creditSummary = detailData?.companyProfile?.companyProfile?.creditSummary;

  if (Object.keys(creditSummary ?? {}).length) {
    for (let propertyName of CREDIT_SUMMARY_NUMBER_FIELDS) {
      let value = lodash.get(creditSummary, propertyName.split('.'));
      if (value && !value?.toString().includes(',')) {
        lodash.set(creditSummary, propertyName.split('.'), formatNumber(value));
      }
    }
  }

  const fetchDetail = async (params: detailParams) => {
    setDetailIsLoading(true);
    try {
      let basicDetail = await companyReportDetailService.getBasicDetail({
        companyCode: params.companyCode,
        countryISO: params.countryISO,
        threshold: params.threshold,
        maxLevel: params.maxLevel,
        screening: params.screening,
        ...(params.stateCode && { stateCode: params.stateCode }),
        ...(params.regAuth && { regAuth: params.regAuth }),
      });

      if (basicDetail?.data?.data) {
        setDetailData(basicDetail.data.data);
        setIsMonitored(basicDetail.data.data?.companyProfile?.isMonitored);
        let toolTip = getRegistrationNumberTooltip(basicDetail.data.data);
        setRegistrationNumberTooltip(toolTip);

        const companyName = basicDetail?.data?.data?.companyProfile?.companyProfile?.companyName;
        const allAvailbleRegistratnNumbers =
          basicDetail?.data?.data?.companyProfile?.companyProfile?.allAvailableRegistrationNumbers;

        let displayRegNo = searchParams.get('displayRegNo');
        let preferredRegistrationNumber = getPreferredRegistrationNumber(
          allAvailbleRegistratnNumbers,
          companyName,
          params.countryISO,
        );

        if (displayRegNo === 'undefined' || displayRegNo === undefined || displayRegNo === '') {
          setRegistrationNumber(preferredRegistrationNumber);
        } else {
          setRegistrationNumber(displayRegNo);
        }
      }
    } catch (err) {
      console.log('[E] error getting basic detail', err);
      handleUBOErrors(err);
    } finally {
      if (searchParams.get('screening') !== 'on') {
        setDetailIsLoading(false);
      }
    }
  };

  const getOwnershipStructureImage = async (): Promise<string | undefined> => {
    await document.fonts.ready;
    const svg = document.querySelector<SVGSVGElement>('#d3-container svg');
    const group = svg?.querySelector<SVGGElement>('g');
    if (!svg || !group) return undefined;

    const bounds = group.getBBox();
    if (!bounds.width || !bounds.height) return undefined;

    const width = 865;
    const height = 350;
    const resolution = 4;
    const copy = svg.cloneNode(true) as SVGSVGElement;
    copy.setAttributeNS('http://www.w3.org/2000/xmlns/', 'xmlns', 'http://www.w3.org/2000/svg');
    copy.setAttribute('width', String(width * resolution));
    copy.setAttribute('height', String(height * resolution));
    copy.setAttribute('viewBox', `0 0 ${width} ${height}`);

    const scale = Math.min((width - 40) / bounds.width, (height - 40) / bounds.height);
    const copiedGroup = copy.querySelector<SVGGElement>('g')!;
    copiedGroup.style.removeProperty('transform');
    copiedGroup.setAttribute(
      'transform',
      `translate(${20 - bounds.x * scale}, ${20 - bounds.y * scale}) scale(${scale})`,
    );

    const originalText = svg.querySelectorAll('text');
    copy.querySelectorAll('text').forEach((text, index) => {
      const style = window.getComputedStyle(originalText[index]);
      text.style.fontFamily = style.fontFamily;
      text.style.fontWeight = style.fontWeight;
      text.style.fontStyle = style.fontStyle;
      text.style.fontSize = '10px';
    });

    // SVG images cannot reliably fetch external icons when drawn onto a canvas.
    const embeddedIcons = new Map<string, string>();
    for (const icon of Array.from(copy.querySelectorAll('image'))) {
      const href = icon.getAttribute('href') || icon.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
      if (!href) continue;
      let dataUrl = embeddedIcons.get(href);
      if (!dataUrl) {
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin) throw new Error('External chart icons are not supported');
        const result = await fetch(url.href);
        if (!result.ok) throw new Error('Could not load an ownership chart icon');
        const blob = await result.blob();
        if (blob.type.split(';')[0].trim() !== 'image/svg+xml') throw new Error('Invalid ownership chart icon');
        dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('Could not read an ownership chart icon'));
          reader.readAsDataURL(blob);
        });
        embeddedIcons.set(href, dataUrl);
      }
      icon.setAttribute('href', dataUrl);
      icon.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', dataUrl);
    }

    const svgUrl = URL.createObjectURL(
      new Blob([new XMLSerializer().serializeToString(copy)], { type: 'image/svg+xml;charset=utf-8' }),
    );
    try {
      const image = new Image();
      image.src = svgUrl;
      await image.decode();
      const canvas = document.createElement('canvas');
      canvas.width = width * resolution;
      canvas.height = height * resolution;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Could not create the ownership chart image');
      context.scale(resolution, resolution);
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  };

  const getPDF = async (detailData: any) => {
    setPdfIsLoading(true);
    try {
      detailData.custom = detailData.custom || {};
      detailData.custom.displayRegNo = searchParams.get('displayRegNo');
      detailData.custom.countryISO = searchParams.get('countryISO');
      detailData.custom.registrationDate = formatDate(
        detailData?.companyProfile?.companyProfile?.registrationDate,
      );
      detailData.custom.nominalShareCapital = getNominalShareCapital(
        detailData?.companyProfile?.companyProfile?.directorAndShareDetails?.nominalShareCapital,
      );

      const directorAndShareDetails =
        detailData?.companyProfile?.companyProfile?.directorAndShareDetails;
      detailData.custom.issuedShareCapital = directorAndShareDetails?.issuedShareCapital?.value
        ? `${formatNumber(directorAndShareDetails?.issuedShareCapital.value)} (${
            directorAndShareDetails?.issuedShareCapital.currency
          })`
        : DEFAULT_EMPTY_VALUE;

      detailData.custom.screenedEntitiesFormatted = [
        ...screenedBusinessEntitiesFormatted,
        ...screenedIndividualEntitiesFormatted,
      ];

      detailData.custom.directorsFormatted = directorsFormatted;
      detailData.custom.beneficialOwnersFormatted = beneficialOwnersFormatted;
      detailData.custom.beneficialOwnersRelationsFormatted = beneficialOwnersRelationsFormatted;
      detailData.custom.extendedGroupStructureFormatted = extendedGroupStructureFormatted;
      detailData.custom.financialInformation = financialInformation;
      detailData.custom.creditSummary = creditSummary; // It's undefined and handled in the PDF report HTML for DnB

      detailData.custom.pdfGenerationUserNames = `${currentUser?.first_name} ${currentUser?.last_name}`;
      detailData.custom.pdfGenerationDate = pdfDate(pdfReviewDate);

      // Add the ownership structure warnings to the PDF report as well
      detailData.custom.ownershipStructureWarnings = detailData?.companyProfile?.companyProfile?.ownershipStructure?.warnings || [];

      delete detailData.custom.ownershipStructure;
      const ownershipStructureImage = await getOwnershipStructureImage();
      if (ownershipStructureImage) {
        detailData.custom.ownershipStructureImage = ownershipStructureImage;
      } else {
        delete detailData.custom.ownershipStructureImage;
      }

      // Add the CRA details to the report if the user has cra enabled
      if (currentUser?.cra_visible) {
        // And adding the CRA object to the request
        detailData.custom.craDetails = CRADetails;
      }

      let pdfReportResponse = await companyReportDetailService.getCompanyReportPDF(detailData);

      const blob = new Blob([pdfReportResponse.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'company_report.pdf'); // The filename you want to download as

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      notify.error('Error generating PDF');
    } finally {
      setPdfIsLoading(false);
    }
  };

  const fetchCraDefaultValues = async () => {
    try {
      const craDefaultValuesResponse = await companyReportsAxiosInstance.get(
        '/cra/default-values',
        {},
      );
      // keeping for debugging purposes
      console.log(craDefaultValuesResponse?.data?.data);
      setCraDefaultValues(craDefaultValuesResponse?.data?.data || {});
    } catch (error) {
      console.log('[E] error getting basic detail', error);
      notify.error('Error getting CRA default values');
    }
  };

  const monitorDUNS = async (dun: string) => {
    setIsMonitoringLoading(true);

    try {
      await dnbMonitoringService.subscribeForDUN(dun);
      notify.success(`You have successfully started monitoring ${detailData?.companyProfile?.companyProfile?.companyName}`);
      setIsMonitored(true);
      setIsMonitoringLoading(false);
    } catch (error) {
      console.log('[E] Error subscribing for DUNS', error);
      notify.error(`An error occurred while attempting to start monitoring ${detailData?.companyProfile?.companyProfile?.companyName}`);
      setIsMonitoringLoading(false);
    }
  };

  const stopMonitoringDUNS = async (dun: string) => {
    setIsMonitoringLoading(true);

    try {
      await dnbMonitoringService.unsubscribeForDUN(dun);
      notify.success(`You have successfully stopped monitoring ${detailData?.companyProfile?.companyProfile?.companyName}`);
      setIsMonitored(false);
      setIsMonitoringLoading(false);
    } catch (error) {
      console.log('[E] Error unsubscribing for DUNS', error);
      notify.error(`An error occurred while attempting to stop monitoring ${detailData?.companyProfile?.companyProfile?.companyName}`);
      setIsMonitoringLoading(false);
    }
  };

  // to fixed problem call twice times
  useEffectOnce(() => {
    fetchDetail({
      companyCode: searchParams.get('companyCode')!,
      countryISO: searchParams.get('countryISO')!,
      screening: searchParams.get('screening')!,
      stateCode: searchParams.get('stateCode')!,
      threshold: 25,
      maxLevel: 3,
      ...(searchParams.get('regAuth') && { regAuth: searchParams.get('regAuth')! }),
    });
    fetchCraDefaultValues();
  });

  return (
    <>
      <div className="row">
        <div className="col-lg-12">
          <div className="card-title d-flex justify-content-between align-items-center">
            <h4 className="color-monoIntense fw-medium">
              <Link to="/company-report">
                <button className="back-btn">
                  <img
                    src={require('src/assets/images/icons/back-arrow.svg').default}
                    alt="Back Arrow"
                  />
                </button>
              </Link>
              Company Search <span className="fw-bold color-green">/ </span>
              {detailData?.companyProfile?.companyProfile?.companyName || DEFAULT_EMPTY_VALUE}
            </h4>

            {/* Show the monitoring related button if the provider is DnB and the company code (DUNS) is available */}
            {/* As well as if the dnb_monitor_visible is toggeld in the DB for that user */}
            {
              detailData.source === PROVIDER_SOURCES.DNB
              && detailData?.companyProfile?.companyProfile?.companyCode
              && currentUser?.dnb_monitor_visible ? (
              isMonitored ? (
                <button
                  className={`ml-2 button btn-ghost button-with-image-and-text button-with-loader ${isMonitoringLoading ? "button-with-loader-loading" : ""}`}
                  onClick={() => {
                    stopMonitoringDUNS(detailData?.companyProfile?.companyProfile?.companyCode);
                  }}
                >
                  {/* Not using the img approach to be able to change the color on hover */}
                  {/* Intentionally leaving the eye-hidden.svg in icons in case it's needed */}
                  <span
                    className={isMonitoringLoading ? "button-with-loader-hidden" : ""}
                  >
                    <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M3.00352 0.0127331C3.26571 0.0104547 3.51832 0.111249 3.70692 0.293407L5.48792 2.07441C6.88642 1.36626 8.43235 0.998281 9.99992 1.00041C14.4779 1.00041 18.2679 3.94341 19.5419 8.00041C18.9432 9.90033 17.7914 11.5785 16.2339 12.8204L17.7069 14.2934C17.8891 14.482 17.9899 14.7346 17.9876 14.9968C17.9853 15.259 17.8801 15.5098 17.6947 15.6952C17.5093 15.8806 17.2585 15.9858 16.9963 15.9881C16.7341 15.9904 16.4815 15.8896 16.2929 15.7074L2.29292 1.70741C2.11076 1.5188 2.00997 1.2662 2.01224 1.00401C2.01452 0.741809 2.11969 0.490997 2.3051 0.305589C2.49051 0.12018 2.74132 0.0150115 3.00352 0.0127331ZM7.96792 4.55341C7.96771 4.55353 7.96813 4.55328 7.96792 4.55341V4.55341ZM7.96792 4.55341L9.48192 6.06841C9.82101 5.97836 10.1778 5.97895 10.5166 6.07014C10.8554 6.16132 11.1643 6.33989 11.4124 6.58797C11.6604 6.83606 11.839 7.14495 11.9302 7.48374C12.0214 7.82252 12.022 8.17932 11.9319 8.51841L13.4459 10.0324C13.8969 9.26843 14.0811 8.3762 13.9696 7.49608C13.858 6.61596 13.4571 5.7979 12.8297 5.17059C12.2024 4.54327 11.3844 4.1423 10.5042 4.03076C9.62437 3.91924 8.73176 4.10269 7.96792 4.55341Z" fill="#11BC97"/>
                      <path d="M9.75001 11.9924L12.454 14.6974C11.669 14.8954 10.847 15.0004 10 15.0004C5.52301 15.0004 1.73201 12.0574 0.458008 8.00043C0.852357 6.74735 1.49022 5.58444 2.33501 4.57843L6.00801 8.25143C6.06918 9.22373 6.4831 10.1403 7.17206 10.8291C7.86103 11.5178 8.77769 11.9315 9.75001 11.9924Z" fill="#11BC97"/>
                    </svg>

                    Stop Monitoring
                  </span>

                  <span
                    className={`button-with-loader-loader ${isMonitoringLoading ? "" : "button-with-loader-hidden"}`}
                  >
                    <img
                      className="loading-icon cr-animated-spinner"
                      src={require('src/assets/images/icons/company_detail_spinner_transparent.gif')}
                      alt="Detail loader"
                    />
                  </span>
                </button>
              ) : (
                <button
                  className={`ml-2 button btn-with-icon button-with-image-and-text button-with-loader ${isMonitoringLoading ? "button-with-loader-loading" : ""}`}
                  onClick={() => {
                    monitorDUNS(detailData?.companyProfile?.companyProfile?.companyCode);
                  }}
                >
                  <span
                    className={isMonitoringLoading ? "button-with-loader-hidden" : ""}
                  >
                    <img
                      src={require('src/assets/images/icons/eye-visible.svg').default}
                      alt=""
                    />

                    Monitor
                  </span>

                  <span
                    className={`button-with-loader-loader ${isMonitoringLoading ? "" : "button-with-loader-hidden"}`}
                  >
                    <img
                      className="loading-icon cr-animated-spinner"
                      src={require('src/assets/images/icons/company_detail_spinner_transparent.gif')}
                      alt="Detail loader"
                    />
                  </span>
                </button>
              )
            ) : null}
          </div>
        </div>

        {/*============================  General Information =======================================*/}
        <div data-testid="company-report-detail-company-info" className="col-lg-6 company-details">
          <div className="card-title">
            <h4>{COMPANY_INFORMATION_HEADING}</h4>
          </div>
          <div className="card h-100">
            <div className="row">
              <div className="col my-auto">
                <h4 className="color-darkBlue fw-medium">
                  {detailData?.companyProfile?.companyProfile?.companyName || DEFAULT_EMPTY_VALUE}
                </h4>

                {(pdfDownloadBtn || currentUser?.screening_disabled) && (
                  <p
                    onClick={() => {
                      getPDF(detailData);
                    }}
                    className="color-green fs-14 m-0 cursor-pointer text-and-loader-container"
                  >
                    <img
                      src={require('src/assets/images/icons/doc-download-green.svg').default}
                      alt=""
                    />
                    Company Report
                    {pdfIsLoading && (
                        <img
                          className="loading-icon cr-animated-spinner"
                          src={require('src/assets/images/icons/company_detail_spinner_transparent.gif')}
                          alt="Detail loader"
                        />
                    )}
                  </p>
                )}
              </div>
              <div className="col my-auto pl-0">
                {detailIsLoading ? (
                    <img
                      className="loading-icon cr-animated-spinner"
                      src={require('src/assets/images/icons/company_detail_spinner_transparent.gif')}
                      alt="Detail loader"
                    />
                  ) : null
                }

                {/* Hide if the user doesn't have the appropriate cra_visible flag */}
                {currentUser?.cra_visible && CRADetails.overallRiskRating !== INITIAL_BADGE_VALUE ? (
                    <RiskRating match={CRADetails.overallRiskRating} riskSuffix={true} />
                  ) : null
                }
              </div>
            </div>
            <table className="mt-4">
              <tbody>
                <tr>
                  <th>Registered Name</th>
                  <td className="company-registered-name-value">
                    {detailData?.companyProfile?.companyProfile?.companyName || DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
                <tr>
                  <th>Registration Number</th>
                  <td className="company-registered-number-value" title={registrationNumberTooltip}>
                    {registrationNumber}
                  </td>
                </tr>
                <tr>
                  <th>Registered Address</th>
                  <td>
                    {detailData?.companyProfile?.companyProfile?.addresses[0]?.addressInOneLine ||
                      DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
                <tr>
                  <th>Country of Registration</th>
                  <td>{searchParams.get('countryISO') ?? DEFAULT_EMPTY_VALUE}</td>
                </tr>
                <tr>
                  <th>Entity Type</th>
                  <td>
                    {detailData?.companyProfile?.companyProfile?.legalForm || DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
                <tr>
                  <th>Number of Employees</th>
                  <td>{numberOfEmployees}</td>
                </tr>
                <tr>
                  <th>Date of Incorporation</th>
                  <td>
                    {formatDate(detailData?.companyProfile?.companyProfile?.registrationDate) ||
                      DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td data-testid="company-status">
                    {detailData?.companyProfile?.companyProfile?.companyStatus ||
                      DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {/*==========================================================================================*/}

        {/*================================ Contact Details ========================================*/}
        <div
          data-testid="company-report-detail-activity-and-contacts"
          className="col-lg-6 company-details contact"
        >
          <div className="card-title">
            <h4>ACTIVITY & CONTACT DETAILS</h4>
          </div>
          <div className="card h-100">
            <table>
              <tbody>
                <tr>
                  <th>Trading Name</th>
                  <td>
                    {detailData?.companyProfile?.companyProfile?.registeredCompanyName ||
                      DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
                <tr>
                  <th>Trading Address</th>
                  <td>
                    {detailData?.companyProfile?.companyProfile?.tradingAddresses?.length
                      ? detailData?.companyProfile?.companyProfile?.tradingAddresses[0]
                          ?.addressInOneLine
                      : DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
                <tr>
                  <th>VAT Number</th>
                  <td>
                    {detailData?.companyProfile?.companyProfile?.vatNumber || DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
                <tr>
                  <th>Industry / Sector</th>
                  <td>
                    {detailData?.companyProfile?.companyProfile?.sector || DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
                <tr>
                  <th>Activity Code</th>
                  <td>{detailData?.companyProfile?.companyProfile?.activity[0]?.code}</td>
                </tr>
                <tr>
                  <th>Activity Description</th>
                  <td>{detailData?.companyProfile?.companyProfile?.activity[0]?.description}</td>
                </tr>
                <tr>
                  <th>Phone Number</th>
                  <td className="company-phone-number">
                    {Array.isArray(phoneNumbers) && phoneNumbers.length > 0 && phoneNumbers[0]
                      ? phoneNumbers[0]
                      : DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
                <tr>
                  <th>Website</th>
                  <td className="company-websites">
                    {Array.isArray(websites) && websites.length > 0 && websites[0]
                      ? websites[0]
                      : DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
                <tr>
                  <th>Email</th>
                  <td className="company-emails">
                    {Array.isArray(companyEmails) && companyEmails.length > 0 && companyEmails[0]
                      ? companyEmails[0]
                      : DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
                <tr>
                  <th>Other Address</th>
                  <td>
                    {detailData?.companyProfile?.companyProfile?.otherAddresses?.length
                      ? detailData?.companyProfile?.companyProfile?.otherAddresses[0]
                          ?.addressInOneLine
                      : DEFAULT_EMPTY_VALUE}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {/*==========================================================================================*/}

        <CompanyDetails
          detailData={detailData}
          updateCRADetails={updateCRADetails}
          setDetailIsLoading={setDetailIsLoading}
          updateScreenedEntities={updateScreenedEntities}
          addDownloadPdfButton={addDownloadPdfButton}
          craDefaultValues={craDefaultValues}
        />
      </div>
    </>
  );
};

export default CompanyReportDetailPage;
